import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { appendCriteriaContains, appendPageable, readPagedRows } from '@/shared/utils/spring-api';
import { provinceDefaultValue } from '../model/province.model';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const apiUrl = '/api/provinces';
function buildProvincesUrl(params) {
    const q = new URLSearchParams();
    appendPageable(q, params?.page, params?.size ?? 1000, params?.sort ?? 'id,asc');
    appendCriteriaContains(q, 'name', params?.name);
    const qs = q.toString();
    return qs ? `${apiUrl}?${qs}` : apiUrl;
}
const initialState = {
    loading: false,
    errorMessage: null,
    provinces: [],
    province: provinceDefaultValue,
    updating: false,
    updateSuccess: false,
    totalItems: 0,
};
export const getProvinces = createAsyncThunk('provinceManagement/fetch_provinces', async (params) => axios.get(buildProvincesUrl(params)), { serializeError: serializeAxiosError });
export const getProvince = createAsyncThunk('provinceManagement/fetch_province', async (id) => axios.get(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const getProvinceCount = createAsyncThunk('provinceManagement/fetch_province_count', async () => axios.get(`${apiUrl}/count`), { serializeError: serializeAxiosError });
export const createProvince = createAsyncThunk('provinceManagement/create_province', async (entity) => axios.post(apiUrl, entity), { serializeError: serializeAxiosError });
export const updateProvince = createAsyncThunk('provinceManagement/update_province', async (entity) => {
    const result = await axios.put(`${apiUrl}/${entity.id}`, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const patchProvince = createAsyncThunk('provinceManagement/patch_province', async (entity) => {
    const { id, ...partial } = entity;
    const result = await axios.patch(`${apiUrl}/${id}`, partial);
    return result;
}, { serializeError: serializeAxiosError });
export const deleteProvince = createAsyncThunk('provinceManagement/delete_province', async (id) => axios.delete(`${apiUrl}/${id}`), { serializeError: serializeAxiosError });
export const ProvinceManagementSlice = createSlice({
    name: 'provinceManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return initialState;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getProvince.fulfilled, (state, action) => {
            state.loading = false;
            state.province = action.payload.data;
        })
            .addCase(getProvinceCount.fulfilled, (state, action) => {
            state.totalItems = action.payload.data;
        })
            .addCase(deleteProvince.fulfilled, (state) => {
            state.updating = false;
            state.updateSuccess = true;
            state.province = provinceDefaultValue;
        })
            .addMatcher(isFulfilled(getProvinces), (state, action) => {
            state.loading = false;
            const { rows, total } = readPagedRows(action.payload.data, action.payload.headers);
            state.provinces = rows;
            state.totalItems = total;
        })
            .addMatcher(isFulfilled(createProvince, updateProvince, patchProvince), (state, action) => {
            state.updating = false;
            state.updateSuccess = true;
            state.province = action.payload.data;
        })
            .addMatcher(isPending(getProvinces, getProvince), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.loading = true;
        })
            .addMatcher(isPending(createProvince, updateProvince, patchProvince, deleteProvince), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isRejected(getProvinces, getProvince, getProvinceCount, createProvince, updateProvince, patchProvince, deleteProvince), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset } = ProvinceManagementSlice.actions;
export default ProvinceManagementSlice.reducer;
